import { type AnalyticsEvent } from '@autone/analytics';
import { useState } from 'react';
import { type Control, type FieldValues, type Path } from 'react-hook-form';

import { useDebouncedValue } from '../../../../hooks';
import { type GetCoreProductBrandsRequest } from '../../../../redux/services';
import { useGetCoreProductBrandsQuery } from '../../../../redux/services/core';
import { TailwindFormField } from '../../../../tailwind';
import { MultiSelectDropdown } from '../../../multi-select-dropdown';
import { MultiselectItemWithId } from '../../CoreApiMultiselectItems';
import { useMultiselectApiFilters } from '../../hooks';
import { type GetMultiselectMapper } from '../../types';
import {
  getConcatenatedOptionId,
  getScopeItemFromConcatenatedId,
  SERVER_SIDE_SEARCH_DROPDOWN_LIMIT,
} from '../../utilities';
import { getRelevantCoreProductFilterKeys } from '../utilities/core-product-filter-keys';

type BrandFilters = GetCoreProductBrandsRequest['body']['filters'];

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  catalogueMap?: GetMultiselectMapper<BrandFilters['catalogue'], T>;
  productMap?: GetMultiselectMapper<BrandFilters['product'], T>;
  productGroupsMap?: GetMultiselectMapper<BrandFilters['product_groups'], T>;
  locationClustersMap?: GetMultiselectMapper<
    BrandFilters['location_cluster'],
    T
  >;
  disabled?: boolean;
  analyticsEvent?: AnalyticsEvent;
}

export const CoreProductBrandsMultiselect = <T extends FieldValues>({
  control,
  name,
  catalogueMap,
  productMap,
  productGroupsMap,
  locationClustersMap,
  disabled,
  analyticsEvent,
}: Props<T>) => {
  const [searchTerm, setSearchTerm] = useState('');

  const catalogueFilters = useMultiselectApiFilters({
    control,
    map: catalogueMap,
    relevantFilterKeys: ['seasons', 'events'],
  });

  const productFilters = useMultiselectApiFilters({
    control,
    map: productMap,
    relevantFilterKeys: getRelevantCoreProductFilterKeys(['brands']),
  });

  const productGroupsFilters = useMultiselectApiFilters({
    control,
    map: productGroupsMap,
    relevantFilterKeys: ['groups'],
  });

  const locationClustersFilters = useMultiselectApiFilters({
    control,
    map: locationClustersMap,
    relevantFilterKeys: ['location_clusters'],
  });

  const debouncedSearchTerm = useDebouncedValue({
    value: searchTerm,
    defaultValue: '',
  });

  const { data, isLoading } = useGetCoreProductBrandsQuery({
    query: {
      limit: SERVER_SIDE_SEARCH_DROPDOWN_LIMIT,
      search_term: debouncedSearchTerm,
    },
    body: {
      filters: {
        product: productFilters,
        catalogue: catalogueFilters,
        product_groups: productGroupsFilters,
        location_cluster: locationClustersFilters,
        size_run: {},
        size: {},
      },
    },
  });

  return (
    <TailwindFormField
      control={control}
      name={name}
      render={({ field: { value, onChange, disabled: fieldDisabled } }) => (
        <MultiSelectDropdown
          className="h-fit"
          loading={isLoading}
          options={data?.items ?? []}
          getOptionId={getConcatenatedOptionId}
          getOptionLabel={(option) => option?.description}
          onSearchTermChange={setSearchTerm}
          selected={value?.map(getConcatenatedOptionId) ?? []}
          onSelectedChange={(ids) =>
            onChange(
              ids.length ? ids.map(getScopeItemFromConcatenatedId) : null,
            )
          }
          renderOption={(option) => <MultiselectItemWithId {...option} />}
          disabled={disabled || fieldDisabled}
          analyticsEvent={analyticsEvent}
        />
      )}
    />
  );
};
